import { useEffect, useRef } from 'react'
import { useConnectionStore } from '@/stores/connectionStore'
import { getAgentById } from '@/services/api'
import { registerAgentFailureCallback } from '@/services/agentProxyService'

// Poll interval for the happy-path background tick.
const POLL_INTERVAL_MS = 10_000
// After N consecutive failures of the status endpoint itself, we treat the
// agent as unreachable and clear the active connection. This tolerates
// transient hiccups without leaving a dead connection showing "Active".
const MAX_CONSECUTIVE_FAILURES = 2

/**
 * Centralised monitor for the active connection's agent health.
 *
 * Responsibilities:
 *   1. Poll `getAgentById` every 10s while an `activeConnection` with an
 *      `agentId` exists; update `connectionHealth` based on the response.
 *   2. Clear `activeConnection` when the agent is confirmed offline or the
 *      status endpoint itself fails N times in a row.
 *   3. Register a callback with `AgentProxyService` so any failed agent-proxy
 *      request triggers an immediate out-of-band recheck — closing the 10s
 *      gap between a failed user action and the UI reflecting it.
 *
 * Mount this **once** at the app-layout level so it survives route changes
 * and sidebar collapse.
 */
export function useAgentHealthMonitor(): void {
  const activeConnection = useConnectionStore(s => s.activeConnection)
  const setConnectionHealth = useConnectionStore(s => s.setConnectionHealth)
  const setActiveConnection = useConnectionStore(s => s.setActiveConnection)

  // Track the most recent agentId so the failure callback can compare without
  // re-registering on every render.
  const currentAgentIdRef = useRef<string | null>(null)
  // Holds the imperative `recheckNow` for the current agent so the failure
  // callback can trigger it out-of-band.
  const recheckRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    currentAgentIdRef.current = activeConnection?.agentId ?? null
  }, [activeConnection?.agentId])

  // Register the agent-failure callback once for the lifetime of the monitor.
  useEffect(() => {
    registerAgentFailureCallback((agentId) => {
      // Only react if the failure is for the currently active agent.
      if (agentId !== currentAgentIdRef.current) return
      // Optimistically flip to 'agent_offline' right away. If it turns out the
      // agent is still fine, the recheck will heal back to 'healthy' within
      // the same tick; but if the backend hasn't yet noticed the agent is
      // gone (heartbeat timeout), we don't want the UI to keep claiming
      // "Active" for another 10s.
      setConnectionHealth('agent_offline')
      const recheck = recheckRef.current
      if (recheck) recheck()
    })
    return () => {
      registerAgentFailureCallback(null)
    }
  }, [setConnectionHealth])

  // Polling loop, keyed on the active agent.
  useEffect(() => {
    const agentId = activeConnection?.agentId
    if (!agentId) {
      recheckRef.current = null
      return
    }

    let cancelled = false
    let consecutiveFailures = 0

    const probe = async () => {
      try {
        const { data: agent } = await getAgentById({
          path: { agent_id: agentId },
          throwOnError: true,
        })
        if (cancelled) return

        if (agent.connection_status === 'connected') {
          consecutiveFailures = 0
          setConnectionHealth('healthy')
        } else {
          // Agent reached us but reports non-connected status: definitive offline.
          consecutiveFailures = 0
          setConnectionHealth('agent_offline')
          setActiveConnection(null)
        }
      } catch (error) {
        if (cancelled) return
        consecutiveFailures += 1
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          setConnectionHealth('error')
          setActiveConnection(null)
        } else {
          // Don't flip UI on the first blip — next tick (or a reactive
          // recheck triggered by a failed agent-proxy call) will either
          // recover or escalate.
          console.warn('[useAgentHealthMonitor] Agent status probe failed:', error)
        }
      }
    }

    // Expose imperative recheck so AgentProxyService failures can trigger
    // us without waiting for the next interval tick.
    recheckRef.current = () => { void probe() }

    // Fire immediately, then poll.
    void probe()
    const intervalId = setInterval(() => { void probe() }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      recheckRef.current = null
      clearInterval(intervalId)
    }
  }, [activeConnection?.agentId, setConnectionHealth, setActiveConnection])
}
